import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, obj = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    const adminContext = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    const token = adminContext
      ? localStorage.getItem("aurum_admin_token") || localStorage.getItem("aurum_token")
      : localStorage.getItem("aurum_token");
    if (!token) {
      setUser(false);
      if (!silent) setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me", silent ? { skipMaintenance: true } : undefined);
      if (!adminContext && ["admin", "staff"].includes(data?.role)) {
        localStorage.removeItem("aurum_token");
        setUser(false);
        return null;
      }
      setUser(data);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403 || !silent) {
        if (adminContext) localStorage.removeItem("aurum_admin_token");
        else localStorage.removeItem("aurum_token");
        setUser(false);
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        fetchMe({ silent: true });
      }
    };
    const interval = window.setInterval(refreshIfVisible, 60000);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [fetchMe]);

  useEffect(() => {
    const expire = () => {
      localStorage.removeItem("aurum_token");
      setUser(false);
    };
    window.addEventListener("sophia:auth-expired", expire);
    return () => window.removeEventListener("sophia:auth-expired", expire);
  }, []);

  const login = (token, userObj) => {
    if (["admin", "staff"].includes(userObj?.role)) {
      localStorage.setItem("aurum_admin_token", token);
      localStorage.removeItem("aurum_token");
    } else {
      localStorage.setItem("aurum_token", token);
    }
    setUser(userObj);
  };

  const logout = () => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      localStorage.removeItem("aurum_admin_token");
    }
    localStorage.removeItem("aurum_token");
    setUser(false);
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
